import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { X, Send, Sparkles, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_PROMPTS = [
  "Optimize my current layout",
  "Suggest better component positions",
  "Make this more luxurious",
  "Reduce cost under ₹3,00,000",
  "Add entertainment setup",
  "Best layout for family camping",
  "Check for any issues",
  "Weight distribution tips",
];

export default function AIAssistant({ designContext, onClose }) {
  const [messages, setMessages] = useState([
    { role: 'ai', text: "I'm your Urbania interior design assistant. Ask me to optimize your layout, suggest components, or help with budget planning!" }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: 'user', text: text.trim() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/ai/chat`, {
        message: text.trim(),
        design_context: designContext
      }, { withCredentials: true });
      setMessages(prev => [...prev, { role: 'ai', text: res.data.response }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'ai', text: 'Sorry, I encountered an error. Please try again.' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(input);
  };

  return (
    <div
      data-testid="ai-assistant-panel"
      className="fixed bottom-6 right-[26rem] z-50 w-96 h-[500px] glass-panel rounded-2xl flex flex-col overflow-hidden shadow-2xl"
      style={{ boxShadow: '0 25px 60px -12px rgba(0,0,0,0.6)' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
            <Sparkles className="w-3.5 h-3.5 text-white" />
          </div>
          <div>
            <span className="text-sm font-semibold text-white">AI Design Assistant</span>
            <span className="block text-[10px] text-slate-500 font-jetbrains">GPT-powered</span>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400 hover:text-white h-7 w-7 p-0" data-testid="ai-close-btn">
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 px-4 py-3">
        <div className="space-y-3">
          {messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-testid={`chat-msg-${i}`}>
              <div className={`max-w-[85%] px-3 py-2 rounded-xl text-sm leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-blue-600 text-white rounded-br-sm'
                  : 'bg-slate-800/60 text-slate-300 border border-white/5 rounded-bl-sm'
              }`}>
                <p className="whitespace-pre-wrap">{msg.text}</p>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start" data-testid="ai-loading">
              <div className="bg-slate-800/60 text-slate-400 border border-white/5 px-3 py-2 rounded-xl rounded-bl-sm flex items-center gap-2">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span className="text-xs">Thinking...</span>
              </div>
            </div>
          )}
          <div ref={scrollRef} />
        </div>
      </ScrollArea>

      {/* Quick Prompts */}
      {messages.length <= 2 && (
        <div className="px-4 pb-2">
          <div className="flex flex-wrap gap-1.5">
            {QUICK_PROMPTS.map((p, i) => (
              <button
                key={i}
                data-testid={`quick-prompt-${i}`}
                onClick={() => sendMessage(p)}
                className="text-[10px] px-2.5 py-1 rounded-full bg-slate-800/60 border border-white/5 text-slate-400 hover:text-white hover:border-blue-500/30 transition-colors"
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-3 border-t border-white/5 flex gap-2" data-testid="ai-chat-form">
        <Input
          data-testid="ai-chat-input"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Ask about your design..."
          className="flex-1 h-9 text-xs bg-slate-800/50 border-white/5 text-white placeholder:text-slate-600"
          disabled={loading}
        />
        <Button
          type="submit"
          size="sm"
          disabled={loading || !input.trim()}
          className="bg-blue-600 hover:bg-blue-700 h-9 w-9 p-0"
          data-testid="ai-send-btn"
        >
          <Send className="w-3.5 h-3.5" />
        </Button>
      </form>
    </div>
  );
}
